import { COST_SIMULATION_YEARS } from "@repo/shared";
import type { PdfCurrencyDisplay } from "@repo/shared/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./cost-table-form.i18n";
import { CurrencyDisplayField } from "./currency-display-field";
import type { ComponentFormProps } from "./types";

export type CostTableConfig = {
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's default. */
  currencyDisplay?: PdfCurrencyDisplay;
  showHoldingPeriod?: boolean;
  holdingPeriodYears?: number;
};

type CostTableFormProps = ComponentFormProps<CostTableConfig>;

export function CostTableForm({ config, onChange }: CostTableFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="cost-table-title">{t.title}</Label>
        <Input
          id="cost-table-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cost-table-description">{t.description}</Label>
        <Textarea
          id="cost-table-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <CurrencyDisplayField
        blockType="cost_table"
        value={config.currencyDisplay}
        onChange={(currencyDisplay) => onChange({ ...config, currencyDisplay })}
      />

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="cost-table-show-holding-period">{t.showHoldingPeriod}</Label>
          <p className="text-xs text-muted-foreground">{t.showHoldingPeriodHelp}</p>
        </div>
        <Switch
          id="cost-table-show-holding-period"
          checked={config.showHoldingPeriod ?? false}
          onCheckedChange={(checked) => onChange({ ...config, showHoldingPeriod: checked })}
        />
      </div>

      {config.showHoldingPeriod && (
        <div className="space-y-2">
          <Label htmlFor="cost-table-holding-period-years">{t.holdingPeriodYears}</Label>
          <Input
            id="cost-table-holding-period-years"
            type="number"
            min={2}
            max={COST_SIMULATION_YEARS}
            value={config.holdingPeriodYears ?? 10}
            onChange={(e) => onChange({ ...config, holdingPeriodYears: Number(e.target.value) })}
          />
          <p className="text-xs text-muted-foreground">{t.holdingPeriodYearsHelp}</p>
        </div>
      )}

      <div className="p-3 bg-muted/50 rounded-md">
        <p className="text-xs text-muted-foreground">{t.componentDescription}</p>
      </div>
    </div>
  );
}
